import FallbackPaywall from "@blocks/FallbackPaywall";
import DynamicImu from "@components/Advertisement/DynamicImu";
import CASBlock from "@components/CASBlock";
import Container from "@components/Container/Container";
import DelayedComponentsLocationChange from "@components/DelayedComponents/DelayedComponentsLocationChange";
import PromoteBTGController from "@features/banner/PromoteBTG/controller/PromoteBTGController";
import { useUserInfo } from "@features/mySph/hooks/useUserInfo";
import PosthogFeature from "@features/posthog/PosthogFeature";
import {
  FEATURE_FLAG_DEEPERDIVE_AB_TEST,
  FEATURE_FLAG_DEEPERDIVE_AB_TEST_VARIANT,
} from "@features/posthog/utils/constants";
import TrendingCards from "@features/pulse/components/trendingCards/TrendingCards";
import Deeperdive from "@features/taboola/Deeperdive";
import useBypassDeeperdive from "@features/taboola/hooks/useBypassDeeperdive";
import Taboola from "@features/taboola/Taboola";
import PromoteCNTranslation from "@features/translations/PromoteCNTranslation";
import { useCountry } from "@hooks/useCountry";
import { useGrapeshotData } from "@hooks/useGrapeshot";
import { useInjectClientData } from "@hooks/useInjectClientData";
import usePublishedTime from "@hooks/usePublishedTime";
import ArticleAttachments from "@routes/Article/components/ArticleAttachments";
import ArticleByline from "@routes/Article/components/ArticleByline/ArticleByline";
import ArticleKicker from "@routes/Article/components/ArticleKicker";
import ThriveNewsletter from "@routes/Article/components/ArticleNewsletter/ThriveNewsletter";
import ArticleParagraphElements from "@routes/Article/components/ArticleParagraphElements/ArticleParagraphElements";
import ArticlePurchaseLink from "@routes/Article/components/ArticlePurchaseLink/ArticlePurchaseLink";
import { Versions } from "@routes/Article/components/ArticlePurchaseLink/types";
import ArticleSubscriberLabel from "@routes/Article/components/ArticleSubscriberLabel";
import ArticleSubshareSent from "@routes/Article/components/ArticleSubshare/ArticleSubshareSent";
import ArticleTags from "@routes/Article/components/ArticleTags";
import { useHookUpdateUrlArticle } from "@routes/Article/hooks/useHookUpdateUrlArticle";
import {
  ArticleAuthorType,
  ArticlePaidModeType,
} from "@routes/Article/utils/enums";
import {
  getCdpPaywallModuleId,
  getGAData,
  getIsBrandedContent,
  getTruncatedBody,
  getUrlPathFromAbsoluteUrl,
  isArticleExcludedFromPaywallBySection,
} from "@routes/Article/utils/helpers";
import { CommonArticleLayoutProps } from "@routes/Article/utils/types";
import { useSetReadingHistory } from "@routes/MyAccount/hooks/useSetReadingHistory";
import { IS_PLAYWRIGHT } from "@util/constant";
import { checkIfFromPaidPressRelease, cn } from "@util/helpers";

const MainNewsDefaultForThrive = ({
  article,
  index = 0,
  gsChannels,
  setIsModalOpen,
  alacrityRes,
  isModalOpen = false,
  setArticleObj,
  isGifted = false,
  isPreview = false,
  isNavigate = false,
  isChinese,
  gaVariant,
  isTaboolaEnabled = false,
  trendingCards,
}: CommonArticleLayoutProps): React.ReactElement => {
  const { isBypassEnabled } = useBypassDeeperdive();
  const { isSubscriber, isAnonymous } = useUserInfo();
  const { isMoreThan60DaysAgo, isMoreThan30DaysAgo } = usePublishedTime(
    article?.publishTime
  );
  // De-structure of article so it is cleaner to get the fields.
  const {
    id,
    title,
    kicker,
    paidMode,
    authors,
    publishTime,
    editedTime,
    hasUpdatedTimeDisplayed,
    relatedAttachments,
    sections,
    standfirst,
    sentiment,
    availableTranslations,
  } = article;

  useSetReadingHistory({ articleId: id });

  const urlPath = getUrlPathFromAbsoluteUrl(article.urlPath || "");

  const { isSGUser } = useCountry();

  const { ref } = useHookUpdateUrlArticle(
    urlPath,
    isNavigate,
    article,
    false,
    gaVariant,
    setArticleObj
  );

  const { gsChannels: gsChannelsAPI } = useGrapeshotData({
    urlPath: urlPath,
    index,
    keywords: sentiment?.keywords,
  });

  const isPremium = paidMode === ArticlePaidModeType.PREMIUM;
  const mainSection = sections?.[0];

  const isBrandedContent = getIsBrandedContent(article?.kicker || "");
  const isPaidPressRelease = checkIfFromPaidPressRelease(
    article?.sections || []
  );

  const isTruncated60DaysAgo =
    !isSubscriber && !isPremium && isMoreThan60DaysAgo;
  const isTruncated30DaysAgo = isAnonymous && !isPremium && isMoreThan30DaysAgo;

  const isFullContent =
    isSubscriber || // User is a subscriber
    isGifted || // Article is gifted
    isPreview || // Article is in preview mode
    isArticleExcludedFromPaywallBySection(sections) ||
    isBrandedContent || // Article is branded content
    (!(isTruncated60DaysAgo || isTruncated30DaysAgo) && !isPremium); // User is not affected by registration wall and article is not premium;

  const cdpPaywallModuleId = getCdpPaywallModuleId({
    isTruncated30DaysAgo: isTruncated30DaysAgo || false,
    isTruncated60DaysAgo: isTruncated60DaysAgo || false,
  });

  const partialElements = getTruncatedBody(article.body, 2);
  const bodyElements = isFullContent ? article.body : partialElements;

  const paragraphIndices = bodyElements
    .map((content, idx) => (content.startsWith("<p>") ? idx : null))
    .filter((idx) => idx !== null);
  const totalParagraphs = paragraphIndices.length;

  const isAuthorOnly = !authors?.some(
    (author) => author.type === ArticleAuthorType.CONTRIBUTOR_PROFILE
  );
  const isBylineArticle = !!authors?.some(({ type }) =>
    [ArticleAuthorType.AUTHOR_PROFILE, ArticleAuthorType.AUTHOR].includes(type)
  );

  const purchaseArticleDetails = {
    isDisplayed: isBylineArticle,
    publishedDate: publishTime,
    title,
  };

  useInjectClientData({ gaProps: getGAData(article, false), gaVariant });

  return (
    <>
      <ArticleSubshareSent
        alacrityRes={alacrityRes}
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
      />

      <article
        className={cn("display-type--thrive  w-full py-5", "animate-slideIn")}
        data-index={index}
        data-cueid={id}
        data-url-path={urlPath}
        ref={ref}
      >
        <div className="px-3 md:px-0 md:pt-4">
          <div className="mx-auto mb-4 w-full max-w-[760px]">
            <>
              {isPremium || kicker ? (
                <div className="flex flex-wrap items-center pb-2 md:px-8 md:pb-4">
                  <ArticleKicker
                    kicker={kicker}
                    className="font-base font-light uppercase"
                  />

                  {isPremium && kicker ? (
                    <div
                      className="ml-3 mr-2 font-poppins text-2xl font-light leading-normal tracking-[0.075rem] text-gray-650"
                      data-testid="kicker-subscriber-label-separator"
                    >
                      ·
                    </div>
                  ) : null}

                  <ArticleSubscriberLabel
                    isPremium={isPremium}
                    displayDot={false}
                    rootClassName={"font-base font-light"}
                  />
                </div>
              ) : null}
            </>

            <h1
              data-testid="article-title"
              className="mb-2 font-lct text-4xl font-bold !leading-[1.3] tracking-[-0.045rem] text-gray-850 md:px-8 md:text-10xl"
            >
              {title}
            </h1>

            <>
              {standfirst ? (
                <p
                  className={cn(
                    "mb-4 mt-2 font-lucida text-lg font-medium !leading-8 -tracking-5% text-gray-850 md:px-8 md:text-xl"
                  )}
                >
                  {standfirst}
                </p>
              ) : null}
            </>

            <>
              {isAuthorOnly && authors ? (
                <div
                  className={cn(
                    "relative min-h-[50px] pt-6 text-gray-850 md:mb-6 md:px-8"
                  )}
                >
                  <ArticleByline
                    authors={authors}
                    editedTime={editedTime}
                    updatedTime={publishTime}
                    hasUpdatedTimeDisplayed={hasUpdatedTimeDisplayed}
                    linkParams={
                      new URLSearchParams({ ref: "article-top-byline" })
                    }
                    backdrop="bg-orange-400"
                  />
                </div>
              ) : null}

              {article.displayReadMoreAndNewsletterSignupForm ? (
                <ThriveNewsletter
                  className="mb-8 md:px-8 lg:mb-12"
                  tagLine="Straight to your inbox."
                  liner="Money, career and life hacks to help young adults stay ahead."
                  hasBorderTop
                />
              ) : null}
            </>

            <>
              {!isAuthorOnly ? (
                <div className="mb-6 pt-6 md:px-8">
                  <ArticleByline
                    authors={authors || []}
                    editedTime={editedTime}
                    updatedTime={publishTime}
                    hasUpdatedTimeDisplayed={hasUpdatedTimeDisplayed}
                  />
                </div>
              ) : null}
            </>

            <>
              {!isChinese ? (
                <PromoteCNTranslation
                  className="mb-6 md:px-8"
                  isTranslationAvailable={availableTranslations?.includes(
                    "zh-hans"
                  )}
                  link={urlPath}
                />
              ) : null}
            </>

            <ArticleAttachments
              attachments={relatedAttachments || []}
              className="mb-6 md:mx-8"
            />

            <div
              className={cn(
                "mx-auto mb-4 font-lucida text-xl leading-8 -tracking-5% text-gray-850 md:mb-6 md:px-8",
                {
                  "mb-4 md:mb-6 [&>*:last-child]:relative [&>*:last-child]:after:absolute [&>*:last-child]:after:inset-0 [&>*:last-child]:after:bg-gradient-to-t [&>*:last-child]:after:from-white [&>*:last-child]:after:to-transparent":
                    !isFullContent,
                }
              )}
              data-testid="article-body-container"
            >
              <ArticleParagraphElements
                body={bodyElements || []}
                article={article}
                adsWrapperClass={cn(
                  "w-screen bg-gray-125 py-6 transition-all duration-300 border-y border-gray-175",
                  { "md:!my-12": IS_PLAYWRIGHT }
                )}
                gsChannels={index === 0 ? gsChannels : gsChannelsAPI}
                articleIndex={index}
                isGifted={isGifted}
                className={"mb-4 md:mb-6 [&_a]:text-green-200"}
                isBrandedContent={isBrandedContent}
                isPaidPressRelease={isPaidPressRelease}
                paragraphIndices={paragraphIndices}
                isFullContent={isFullContent}
              />
            </div>
            <>
              {isFullContent ? (
                <ArticleTags
                  tags={article?.tags}
                  className="mb-6 md:px-8"
                  linkParams={
                    new URLSearchParams({ ref: "article-bottom-keyword" })
                  }
                />
              ) : null}
            </>

            <>
              {publishTime && isFullContent ? (
                <div className="no-print mb-6 border-t border-gray-175 py-3 md:mx-8">
                  <ArticlePurchaseLink
                    title={title}
                    publishedDate={publishTime}
                    versions={Versions.version2}
                    displayPurchase={purchaseArticleDetails.isDisplayed}
                    urlPath={urlPath}
                  />
                </div>
              ) : null}
            </>

            <PromoteBTGController />

            <>
              {isSGUser ? (
                <DelayedComponentsLocationChange
                  waitBeforeShow={1000}
                  urlPath={urlPath}
                >
                  <CASBlock
                    cdpId={cdpPaywallModuleId}
                    index={index || 0}
                    className="no-print mb-6 mt-11 md:px-8 lg:-mx-6"
                    fallback={
                      !isSubscriber ? (
                        <FallbackPaywall
                          className="mb-6 transition duration-300 ease-in data-[closed]:opacity-0"
                          isPremium={isPremium}
                        />
                      ) : null
                    }
                  />
                </DelayedComponentsLocationChange>
              ) : null}
            </>

            <>
              {!isFullContent ? (
                <ArticleTags
                  tags={article.tags}
                  className="mb-8 mt-11 md:px-8 lg:-mx-6"
                  linkParams={
                    new URLSearchParams({ ref: "article-bottom-keyword" })
                  }
                />
              ) : null}

              <PosthogFeature
                flagName={FEATURE_FLAG_DEEPERDIVE_AB_TEST}
                variantKey={
                  isBypassEnabled
                    ? undefined
                    : FEATURE_FLAG_DEEPERDIVE_AB_TEST_VARIANT
                }
              >
                <Deeperdive
                  urlPath={article.urlPath}
                  counter={index}
                  isArticlePage
                  gaVariant={gaVariant}
                />
              </PosthogFeature>

              {mainSection && !isPaidPressRelease ? (
                <DynamicImu
                  index={index || 0}
                  totalParagraphs={totalParagraphs}
                  adsWrapperClass={cn(
                    "w-screen bg-gray-125 py-6 transition-all duration-300 border-y border-gray-175",
                    { "md:!my-12": IS_PLAYWRIGHT }
                  )}
                  articleId={article?.id}
                  keywords={article?.tags}
                />
              ) : null}
            </>
          </div>

          {trendingCards && trendingCards.length > 0 ? (
            <Container rootClassName="px-0 no-print">
              <TrendingCards
                trendingCards={trendingCards}
                currentArticleId={id}
                urlPath={urlPath}
              />
            </Container>
          ) : null}

          {isTaboolaEnabled ? (
            <Container rootClassName="px-0 no-print">
              <Taboola
                container={`below-article-thumbnails-${index}`}
                placement={`Below Article Thumbnails-${index}`}
                url={article.urlPath}
                pageType="article"
              />
            </Container>
          ) : null}
        </div>
      </article>
    </>
  );
};

export default MainNewsDefaultForThrive;
